import { useMemo } from 'react'
import { Dialog, Box, Text, Table, useResponsiveValue } from '@machobear/design-system'
import { useTranslation } from 'react-i18next'

type Props = {
  visible: boolean
  closeModal: () => void
}

const RentalSpaceModal = ({ visible, closeModal }: Props) => {
  const { t } = useTranslation()
  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  const data = [
    {
      type: t('Private', 'Private'),
      description: t(
        'l.listing.edit.private_type.text',
        'Exclusive access to the entire property or unit. An entire place usually includes a bedroom, a bathroom, and a kitchen.'
      )
    },
    {
      type: t('Shared', 'Shared'),
      description: t(
        'l.listing.edit.shared_type.text',
        'Exclusive access to part of the property, such as a basement suite or section of a larger unit, with shared access to common areas such as the kitchen and bathroom.'
      )
    },
    {
      type: t('Room', 'Room'),
      description: t(
        'l.listing.edit.room_type.text',
        'The listing is a private bedroom, with a door, that is exclusively used by the tenant, with shared access to common areas like the kitchen and bathroom.'
      )
    }
  ]

  const columns = useMemo(
    () => [
      {
        Header: t('Space', 'Space'),
        accessor: 'type',
        gridWidth: { custom: '150px' },
        disableSortBy: true,
        Cell: (cell: { value: string; row: { original: any } }) => {
          return (
            <Box paddingY="small">
              <Text>{cell?.value}</Text>
            </Box>
          )
        }
      },
      {
        Header: t('Description', 'Description'),
        accessor: 'description',
        gridWidth: { custom: 'auto' },
        disableSortBy: true,
        Cell: (cell: { value: string; row: { original: any } }) => {
          return <Text>{cell?.value}</Text>
        }
      }
    ],
    [t]
  )

  const mobileColumns = useMemo(
    () => [
      {
        Header: t('Description', 'Description'),
        accessor: 'description',
        gridWidth: { custom: '100%' },
        disableSortBy: true,
        Cell: (cell: { value: string; row: { original: any } }) => {
          return (
            <Box paddingY="xsmall" display="flex" flexDirection="column" gap="medium">
              <Text weight="strong">{cell?.row?.original?.type}</Text>
              <Text>{cell?.value}</Text>
            </Box>
          )
        }
      }
    ],
    [t]
  )

  return (
    <Dialog
      width="small"
      id="rental-space-dialog"
      open={visible}
      onClose={closeModal}
      title={t('Rental Space', 'Rental Space')}
    >
      <Table
        data={data}
        columns={isMobile ? mobileColumns : columns}
        width="full"
        itemsSelectedText=""
        clearAllText=""
      />
    </Dialog>
  )
}

export default RentalSpaceModal
